import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * @param {MouseEvent & {target: HTMLElement}} event
   */
  handleClick(event) {
    this.animateAddToCart();
    if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;
    if (!image) return;
    preloadImage(image);
  };

  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;
    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId
 * @property {AddToCartComponent | undefined} addToCartButtonContainer
 * @property {HTMLElement | undefined} addToCartTextError
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer
 * @property {HTMLElement} liveRegion
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Keep UI in sync on load (important if HTML was morphed/updated)
    this.#syncBackorderUI(this.#isEligibleBackorderGift());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #isBackorderEnabled = () => {
    return this.dataset.backorderEnabled === 'true';
  };

  /**
   * ✅ Backorder eligibility (MOST RELIABLE):
   * If the form contains the Back order property input with value, treat as eligible.
   * (Because Liquid adds it ONLY when collection + inventory condition matches.)
   */
  #hasBackorderPropertyInForm = () => {
    if (!this.#isBackorderEnabled()) return false;
    const el = this.querySelector('input[name="properties[Back order]"]');
    return Boolean(el && el.value && el.value.trim().length > 0);
  };

  /**
   * ✅ Fallback eligibility via dataset (if you also pass these attrs).
   */
  #datasetEligibility = () => {
    if (!this.#isBackorderEnabled()) return false;
    const isBackorderCollection = this.dataset.isBackorderCollection === 'true';
    const policy = (this.dataset.variantInventoryPolicy || '').toString();
    const qty = parseInt(this.dataset.variantInventoryQuantity || '999999', 10);
    return isBackorderCollection && policy === 'continue' && qty <= 0;
  };

  /**
   * ✅ Final eligibility
   */
  #isEligibleBackorderGift = () => {
    return this.#hasBackorderPropertyInForm() || this.#datasetEligibility();
  };

  /**
   * ✅ Sync backorder message UI + Back order property input
   * (If your Liquid already renders it, we just ensure it’s correct after variant changes.)
   */
  #syncBackorderUI = (isActive) => {
    const shouldShowBackorder = this.#isBackorderEnabled() && isActive;
    const msg = this.querySelector('.back-order-message');
    if (msg) msg.style.display = shouldShowBackorder ? 'block' : 'none';

    const line = this.querySelector('.back-order-line');
    if (!line) return;

    const existing = line.querySelector('input[name="properties[Back order]"]');

    if (shouldShowBackorder) {
      const value = existing?.value?.trim() || (this.dataset.backorderMessage || 'This item is on backorder. Ships end-August');
      if (!existing) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[Back order]';
        input.value = value;
        line.appendChild(input);
      } else {
        existing.value = value;
      }
    } else {
      if (existing) existing.remove();
    }
  };

  /**
   * Handles submit
   * @param {Event} event
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponent = this.closest('cart-items-component');
    if (cartItemsComponent?.classList.contains('cart-items-disabled')) return;

    // Ensure correct state before FormData is created
    this.#syncBackorderUI(this.#isEligibleBackorderGift());

    const atcBtn = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (atcBtn?.disabled) return;

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    // ✅ Collect section IDs once (avoid appending "sections" multiple times)
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (cartItemComponentsSectionIds.length) {
      formData.set('sections', cartItemComponentsSectionIds.join(','));
      formData.set('sections_url', window.location.pathname);
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.root + 'cart/add.js', {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, 10000);

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')),
              productId: this.dataset.productId,
            })
          );

          return;
        }

        // ✅ Success
        const id = formData.get('id');
        if (!id) throw new Error('Form ID is required');

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
          const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
          const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), 5000);
        }

        this.dispatchEvent(
          new CartAddEvent({}, id.toString(), {
            source: 'product-form-component',
            itemCount: Number(formData.get('quantity') || 1),
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );

        const upsellParentVariantId = formData.get('properties[_Upsell Parent Variant ID]');
        if (upsellParentVariantId) {
          window.FitBootsUpsellDismissals?.clear(id.toString());
        }

        // ✅ FREE GIFT LOGIC (robust)
        const parentVariantId = id.toString();
        const parentQty = Number(formData.get('quantity') || 1);

        // IMPORTANT:
        // - Liquid should pass data-free-product-ids ONLY for allowed collections
        // - Eligibility uses Back order property input (most reliable)
        const freeProductIds = this.dataset.freeProductIds;

        if (this.#isEligibleBackorderGift() && freeProductIds) {
          const ids = freeProductIds.split(',').map((s) => s.trim()).filter(Boolean);
          this.#addFreeProductsToCart(ids, cartItemComponentsSectionIds, parentVariantId, parentQty);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    this.refs.liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    this.refs.liveRegion.textContent = '';
  }

  /**
   * ✅ Add free products PER parent + match quantity
   * - If free gift already exists for THIS parent => add only the missing qty
   * - If exists for another parent => still add (because properties differ)
   *
   * @param {string[]} freeProductVariantIds
   * @param {string[]} cartItemComponentsSectionIds
   * @param {string} parentVariantId
   * @param {number} desiredQty
   */
  async #addFreeProductsToCart(freeProductVariantIds, cartItemComponentsSectionIds, parentVariantId, desiredQty = 1) {
    try {
      const cartResponse = await fetch(Theme.routes.root + 'cart.js');
      const cartData = await cartResponse.json();

      const itemsToAdd = [];

      freeProductVariantIds.forEach((variantIdRaw) => {
        const variantId = variantIdRaw.trim();
        if (!variantId) return;

        // Find existing free line for THIS parent
        const existingLine = cartData.items.find((item) => {
          if (item.variant_id?.toString() !== variantId) return false;
          if (!item.properties) return false;

          const isFree =
            item.properties._Free_Product === 'Yes' ||
            item.properties['_Free Product'] === 'Yes' ||
            item.properties['_Free Product'] !== undefined;

          const parentId =
            item.properties._Parent_Variant_ID ||
            item.properties['_Parent Variant ID'];

          return isFree && parentId && parentId.toString() === parentVariantId.toString();
        });

        const existingQty = existingLine ? Number(existingLine.quantity || 0) : 0;
        const addQty = Math.max(0, Number(desiredQty || 1) - existingQty);

        // Add only missing qty (so it works even if cart already has products)
        if (addQty > 0) {
          itemsToAdd.push({
            id: variantId,
            quantity: addQty,
            properties: {
              '_Free Product': 'Yes',
              '_Parent Variant ID': parentVariantId,
            },
          });
        }
      });

      if (!itemsToAdd.length) return;

      const requestBody = { items: itemsToAdd };

      if (cartItemComponentsSectionIds.length > 0) {
        requestBody.sections = cartItemComponentsSectionIds.join(',');
      }

      const response = await fetch(Theme.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      if (!responseData.status) {
        const sections = responseData.sections || {};

        itemsToAdd.forEach((it) => {
          this.dispatchEvent(
            new CartAddEvent({}, it.id.toString(), {
              source: 'product-form-component',
              itemCount: Number(it.quantity || 1),
              productId: it.id.toString(),
              sections,
              isFreeProduct: true,
            })
          );
        });
      }
    } catch (error) {
      console.error('Error adding free products to cart:', error);
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    variantId.value = event.detail.resource?.id ?? '';

    // keep dataset updated if your Liquid passes them
    if (event.detail.resource) {
      if (event.detail.resource.inventory_quantity !== undefined) {
        this.dataset.variantInventoryQuantity = event.detail.resource.inventory_quantity.toString();
      }
      if (event.detail.resource.inventory_policy !== undefined) {
        this.dataset.variantInventoryPolicy = event.detail.resource.inventory_policy.toString();
      }
    }

    // Sync UI after variant change
    this.#syncBackorderUI(this.#isEligibleBackorderGift());

    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    const offset = { x: rect.width / 2, y: rect.height / 2 };

    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 };
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 };

    let startTime = null;
    const duration = 600;

    this.style.opacity = '1';

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    requestAnimationFrame(animate);
  }
}

function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}

const DISMISSED_UPSELLS_STORAGE_KEY = 'fitboots-dismissed-upsells-v1';

function readDismissedUpsells() {
  try {
    const raw = window.sessionStorage.getItem(DISMISSED_UPSELLS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
}

function writeDismissedUpsells(items) {
  try {
    window.sessionStorage.setItem(DISMISSED_UPSELLS_STORAGE_KEY, JSON.stringify(items));
  } catch (_error) {
    // Ignore storage errors.
  }
}

function normalizeDismissedUpsells(items) {
  const seen = new Set();

  return items
    .map((item) => String(item || '').trim())
    .filter((item) => {
      if (!item) return false;
      if (seen.has(item)) return false;
      seen.add(item);
      return true;
    });
}

function dismissUpsell(upsellVariantId) {
  const items = normalizeDismissedUpsells([...readDismissedUpsells(), upsellVariantId]);
  writeDismissedUpsells(items);
}

function clearDismissedUpsell(upsellVariantId) {
  const nextItems = readDismissedUpsells().filter((item) => String(item) !== String(upsellVariantId));
  writeDismissedUpsells(nextItems);
}

function applyDismissedUpsells(root = document) {
  const dismissedVariantIds = new Set(readDismissedUpsells().map((item) => String(item)));

  root.querySelectorAll('product-cards[data-upsell-parent-variant-id][data-upsell-variant-id]').forEach((card) => {
    const shouldHide = dismissedVariantIds.has(String(card.dataset.upsellVariantId));
    card.hidden = shouldHide;
    card.style.display = shouldHide ? 'none' : '';
  });
}

async function refreshDismissedUpsells() {
  applyDismissedUpsells();
}

window.FitBootsUpsellDismissals = {
  dismiss(upsellVariantId) {
    dismissUpsell(upsellVariantId);
    applyDismissedUpsells();
  },
  clear(upsellVariantId) {
    clearDismissedUpsell(upsellVariantId);
    applyDismissedUpsells();
  },
  refresh() {
    return refreshDismissedUpsells();
  },
};

document.addEventListener(ThemeEvents.cartUpdate, () => {
  window.FitBootsUpsellDismissals.refresh();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.FitBootsUpsellDismissals.refresh();
  });
} else {
  window.FitBootsUpsellDismissals.refresh();
}

// ---------------- Upsell Add to Cart (your existing code) ----------------

window.addAllToCart = async function (event) {
  event.preventDefault();
  event.stopPropagation();

  const cartItemsComponent = this.closest('cart-items-component');
  if (cartItemsComponent?.classList.contains('cart-items-disabled')) return;

  var variantId = this.closest('.cart-upsell-items')?.dataset?.id || this.dataset.id;
  var productId = this.closest('.cart-upsell-items')?.dataset?.productId || this.dataset.productId;

  if (!variantId) return;

  const items = [{ id: variantId, quantity: 1 }];

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });

    if (response.ok) {
      const updatedCartResponse = await fetch('/cart.js');
      const updatedCartData = await updatedCartResponse.json();
      const cartCount = updatedCartData.item_count;

      document.querySelectorAll('.cart-bubble__text').forEach(function (element) {
        setTimeout(function () {
          element.textContent = cartCount;
        }, 500);
      });

      document.dispatchEvent(
        new CartAddEvent({}, variantId.toString(), {
          source: 'cart-items-component',
          itemCount: Number(1),
          productId: productId,
        })
      );
    } else {
      throw new Error('Failed to add items to cart');
    }
  } catch (error) {
    console.error('Error adding to cart:', error);
  }
};

document.addEventListener('click', function (event) {
  const upsellButton = event.target.closest('button.cart-upsell-items');
  if (upsellButton && !upsellButton.disabled) {
    event.preventDefault();
    window.addAllToCart.call(upsellButton, event);
  }
});

document.addEventListener('change', function (event) {
  if (event.target.closest('.cart-upsell-items-add-cart select[name="id"]')) {
    const selectedOption = event.target.selectedOptions[0];
    const variantId = selectedOption.dataset.variant;
    const variantPrice = selectedOption.dataset.price;

    event.target
      .closest('.cart-upsell-items-add-cart')
      .querySelector('.cart-upsell-items')
      .setAttribute('data-id', variantId);

    event.target
      .closest('.cart-upsell-items-details')
      .querySelector('.price-text').textContent = variantPrice;
  }
});
